import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useAmanFlow from "../../../../hooks/useAmanFlow";
import useAmanFormData from "../../../../hooks/useAmanFormData";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import AmanJourneyLayout from "../../layouts/AmanJourneyLayout";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import SignInModal from "../../../../pages/motorInsurancePages/newMotorPolicy/planPages/signInModal";
import AmanBenefitsModal from "../../modals/AmanBenefitsModal";
import AmanSummaryModal from "../../modals/AmanSummaryModal";
import doneIcon from "../../../../assets/images/done.svg";
import amanPlusPlanIcon from "../../../../assets/images/aman-plus-plan.svg";
import amanPlanIcon from "../../../../assets/images/aman-plan.svg";
import listIcon from "../../../../assets/images/list-black-1.svg";

const AMAN_PLAN_SELECTION = "AMAN_PLAN_SELECTION";

const PlanSelectionPage = ({ onClose }) => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage !== "en";
  const { handleNextSubstep } = useAmanFlow();
  const { saveAmanDataInRedux, redux_form_data } = useAmanFormData(
    AMAN_PLAN_SELECTION,
    () => {}
  );
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const isLoggedIn = !!loginTokenInfo;

  const [selectedPlan, setSelectedPlan] = useState(
    redux_form_data?.planId || null
  );
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  const plans = [
    {
      id: "aman",
      name: translate("Aman", "أمَانْ"),
      price: 84,
      priceText: translate("BHD 84", "84 دينار بحريني"),
      icon: amanPlanIcon,
      benefits: [
        translate(
          "Provides access to private hospitals with less waiting time. All treatment stages (consultation, medications, X-rays and tests) are available under one roof.",
          "يوفر لك الوصول إلى المستشفيات الخاصة حيث يكون وقت الانتظار أقل. جميع مراحل العلاج (الاستشارة، الأدوية، الأشعة والفحوصات) متاحة تحت سقف واحد."
        ),
      ],
    },
    {
      id: "aman_plus",
      name: translate("Aman Plus", "Plus أمَانْ"),
      price: 94,
      priceText: translate("BHD 94", "94 دينار بحريني"),
      icon: amanPlusPlanIcon,
      benefits: [
        translate(
          "Provides access to private hospitals with less waiting time. All treatment stages (consultation, medications, X-rays and tests) are available under one roof.",
          "يوفر لك الوصول إلى المستشفيات الخاصة حيث يكون وقت الانتظار أقل. جميع مراحل العلاج (الاستشارة، الأدوية، الأشعة والفحوصات) متاحة تحت سقف واحد."
        ),
        translate("Death of domestic helper", "وفاة العامل المنزلي"),
      ],
      isRecommended: true,
    },
  ];

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    const planData = plans.find((p) => p.id === planId);
    if (planData) {
      saveAmanDataInRedux({
        planId: planId,
        planName: planData.name,
        price: planData.price,
      });
    }
  };

  const handleContinue = async () => {
    const selectedPlanData = plans.find((p) => p.id === selectedPlan);
    if (selectedPlanData) {
      await saveAmanDataInRedux({
        planId: selectedPlan,
        planName: selectedPlanData.name,
        price: selectedPlanData.price,
      });

      if (!isLoggedIn) {
        setShowLoginModal(true);
      } else {
        handleNextSubstep();
      }
    }
  };

  const selectedPlanData = plans.find((p) => p.id === selectedPlan);
  const priceWithVat = selectedPlanData ? selectedPlanData.price * 1.1 : 0;

  return (
    <>
    <AmanJourneyLayout
      is_hide_back_btn={false}
      show_close_btn={false}
      onClose={onClose}
    >
      <SignInModal
        show={showLoginModal}
        setShow={setShowLoginModal}
        cbSuccess={() => {
          setShowLoginModal(false);
          handleNextSubstep();
        }}
      />

      <AmanBenefitsModal
        show={showBenefitsModal}
        onClose={() => setShowBenefitsModal(false)}
      />

      <BottomStickyBtnLayout
        btn_label={translate("Continue", "استمرار")}
        btn_onClick={handleContinue}
        btn_disabled={!selectedPlan}
        show_summary={!!selectedPlan}
        price={priceWithVat}
        onShowSummary={() => setShowSummaryModal(true)}
      >
        <div className="w-[560px] max767:w-full mx-auto ipad:w-[90%] p-5 max767:p-0 max767:px-5 pb-32">
          <div className="mb-6" style={{ textAlign: isRTL ? 'right' : 'center' }}>
            <h1 className="text-[#1A2024] text-2xl font-bold leading-8 tracking-[-0.24px] mb-1">
              {translate("Choose Your Plan", "اختر خطتك")}
            </h1>
            <p className="text-[#1A2024] text-base font-medium leading-6 tracking-[-0.16px]">
              {translate(
                "Select the plan that best suits your needs.",
                "حدد الخطة التي تناسب احتياجاتك بشكل أفضل."
              )}
            </p>
          </div>

          <div className="flex gap-4 justify-center max767:overflow-x-auto max767:snap-x max767:snap-mandatory max767:pb-2 max767:justify-start max767:scrollbar-hide max767:pt-4">
            {plans.map((plan) => (
                <div
                  key={plan.id}
                  dir={isRTL ? "rtl" : "ltr"}
                  onClick={() => handlePlanSelect(plan.id)}
                  className={`relative cursor-pointer rounded-[10px] p-6 w-[260px] min-w-[260px] max767:snap-center flex flex-col gap-4 transition-all mt-4 ${
                    selectedPlan === plan.id
                      ? "bg-[#E3F8FF] border-2 border-[#00719A]"
                      : "bg-white border border-[#DDE2E4] hover:border-[#B0BABF]"
                  }`}
                >
                  {plan.isRecommended && (
                    <div 
                      className="absolute -top-3 left-4 rtl:left-auto rtl:right-4 bg-[#F76659] text-[#FFEFEB] text-sm font-semibold px-2 py-0 rounded-[6px] leading-6 tracking-[-0.084px]"
                    >
                      {translate("Recommended", "موصى بها")}
                    </div>
                  )}

                  <div className="flex items-start justify-between w-full">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <img src={plan.icon} alt={plan.name} className="w-12 h-12" />
                    </div>

                    <div
                      className={`w-5 h-5 rounded-[10px] flex items-center justify-center flex-shrink-0 ${
                        selectedPlan === plan.id
                          ? "bg-[#00A9E7]"
                          : "bg-[#F6F8F9] border border-[#B0BABF]"
                      }`}
                    >
                      {selectedPlan === plan.id && (
                        <div className="w-2 h-2 bg-white rounded-[4px]" />
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 justify-center leading-6 text-[#1A2024] w-full">
                    <p className="font-semibold text-lg tracking-[-0.18px]">
                      {plan.name}
                    </p>
                    <p className="font-normal text-base tracking-[-0.16px]">
                      {plan.priceText}
                    </p>
                  </div>

                  <div className="flex flex-col gap-1 w-full">
                    {plan.benefits.map((benefit, index) => (
                      <div
                        key={index}
                        className="flex gap-2 items-start w-full"
                      >
                        <img
                          src={doneIcon}
                          alt=""
                          className="w-4 h-4 flex-shrink-0 mt-0.5"
                        />
                        <p className="flex-1 font-medium text-xs text-black leading-[1.5]">
                          {benefit}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
            ))}
          </div>

          <div className="flex justify-center mt-6 max767:px-4">
            <button
              onClick={() => setShowBenefitsModal(true)}
              className="bg-white border border-[#DDE2E4] rounded-[10px] px-4 py-2 flex items-center gap-2 max767:w-full max767:justify-center"
              style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}
            >
              <img src={listIcon} alt="" className="w-5 h-5" />
              <span className="text-[#1A2024] text-sm font-bold leading-6 tracking-[-0.084px]">
                {translate(
                  "View full list of benefits & covers",
                  "عرض القائمة الكاملة للفوائد والتغطيات"
                )}
              </span>
            </button>
          </div>
        </div>
      </BottomStickyBtnLayout>
    </AmanJourneyLayout>

    <AmanSummaryModal
      show={showSummaryModal}
      onClose={() => setShowSummaryModal(false)}
    />
  </>
  );
};

export default PlanSelectionPage;
